import { useTranslation } from 'react-i18next';
import {
  addDays,
  addHours,
  addMinutes,
  format,
  fromUnixTime,
  getHours,
  hoursToSeconds,
  isSameDay,
  minutesToSeconds,
  secondsToHours,
  secondsToMinutes,
  startOfDay,
} from 'date-fns';
import add from 'date-fns/add';
import moment from 'moment-timezone';

const getTimeFormat = () =>
  // TODO: Get time format from system setting if available
  '12h';
/**
 *
 * @param {Date} date The date to format into an hour time format
 * @param {String} timeFormat The date format, either 12h or 24h
 * @returns Returns the hour and the am/pm specification if needed. Will only use a or p, for example
 * 5p or 5a will be returned for 5:00 PM and 5:00 AM respectivly.
 */
const getZonedHourTime = (date, timeZone, timeFormat) => {
  if (timeFormat === '12h') {
    return moment(date).tz(timeZone)?.format('ha').slice(0, -1);
  }
  return moment(date).tz(timeZone).format('k');
};

/**
 *
 * @param {Date} date The date to format into an hour time format
 * @param {String} timeFormat The date format, either 12h or 24h
 * @returns Returns the hour and the am/pm specification if needed. Exactly the same as
 * getZonedHourTime but uses AM and PM instead of a and p, respectively with spacing. This
 * is better for accessibility strings.
 */
const getZonedHourTimeAMPM = (date, timeZone, timeFormat) => {
  if (timeFormat === '12h') {
    return moment(date).tz(timeZone)?.format('h A');
  }
  return moment(date).tz(timeZone).format('k');
};

/**
 *
 * @param {Date} date The date to format into an hour time format
 * @returns Returns the number of minutes past the hour of date, or now if date is null.
 */
const getMinuteTime = date => {
  const minuteDate = date ?? new Date();

  return format(minuteDate, 'm');
};

/**
 *
 * @param {Date|number|string} date The date to format into a time format
 * @param {string} timeZone
 * @param {string} timeFormat The date format, either 12h or 24h
 * @returns Returns the hour and minute format for the date, respecting the timeFormat string.
 */
const getZonedTime = (date, timeZone, timeFormat) => {
  if (timeFormat === '12h') {
    return moment(date).tz(timeZone)?.format('h:mma').slice(0, -1);
  }
  return moment(date).tz(timeZone)?.format('k:mm');
};

/**
 *
 * @param {Date} date The date to format into a time format
 * @param {String} timeFormat The date format, either 12h or 24h
 * @returns Returns the hour and minute format for the date, respecting the timeFormat string.
 */
const getLocalTime = (date, timeFormat) => {
  /**
   * h -> 1 - 12 hour format
   * k -> 1 - 24 hour format
   * m -> 0 - 59 minutes
   * aaaaa -> a/p for am and pm
   */
  const hoursPattern = timeFormat === '12h' ? 'h:mmaaaaa' : 'k:mm';

  return format(date, hoursPattern);
};

/**
 * Returns a string for displaying a time range, in the format 10:00-11:00a or 11:00-1:00p for example.
 * The format will follow first time (hour/min) followed by a dash and then the second time, with the a/p
 * included for 12h format.
 * @param {Date} startDate The date representing the start of the time range.
 * @param {Date} endDate The date representing the end of the time range.
 * @param {String} timeFormat The time format, either 12h or 24h. Default is 12h.
 * @returns
 */
const getTimeRange = (startDate, endDate, timeFormat = '12h') => {
  /**
   * h -> 1 - 12 hour format
   * k -> 1 - 24 hour format
   * m -> 0 - 59 minutes
   * aaaaa -> a/p for am and pm
   */
  const startTimeFormat = timeFormat === '12h' ? 'h:mm' : 'k:mm';
  const endTimeFormat = timeFormat === '12h' ? 'h:mmaaaaa' : 'k:mm';

  return `${format(startDate, startTimeFormat)}-${format(endDate, endTimeFormat)}`;
};

/**
 * Used to get a more understandable/readable time for start of something. Uses am/pm instead of a/p,
 * unless timeFormat is `24h`.
 * @param {Date} startDate The date to format
 * @param {String?} timeFormat The time format, either 12h or 24h
 * @returns A strng for the time. Will trim out minutes if 0. For example 2:00 pm will return `2 pm`,
 * but 2:30 pm will return `2:30 pm`.
 */
const getStartTime = (startDate, timeFormat = '12h') => {
  const hasMinutes = format(startDate, 'm') !== '0';
  const startTimeFormat = timeFormat === '12h' ? (hasMinutes ? 'h:mm aaaa' : 'h aaaa') : hasMinutes ? 'k:mm' : 'k';

  return format(startDate, startTimeFormat);
};

const zeroPad = time => (time === 0 ? '00' : time < 10 ? `0${time}` : `${time}`);

/**
 * Formats a duration to the standard hh:mm:ss format.
 * @param {number} duration Duration in seconds.
 * @returns Returns a string duration, with hh:mm:ss foramt. Will pad zeros and only return
 * hours if needed.
 */
const secondsToDuration = duration => {
  if (isNaN(duration)) {
    return '--:--';
  }

  let durationInSeconds = duration;

  const hours = secondsToHours(durationInSeconds);

  durationInSeconds -= hoursToSeconds(hours);

  const minutes = secondsToMinutes(durationInSeconds);

  durationInSeconds -= minutesToSeconds(minutes);

  const seconds = Math.floor(durationInSeconds);

  return hours > 0 ? `${hours}:${zeroPad(minutes)}:${zeroPad(seconds)}` : `${minutes}:${zeroPad(seconds)}`;
};

/**
 * Returns the number of minutes represented by the duration in seconds. For example getMinutes(125) returns 2.
 * Rounds down to nearest minute.
 * @param {Number} duration Duration in seconds
 * @returns Number of minutes in duration seconds, rounded down.
 */
const getMinutes = duration => {
  if (isNaN(duration)) {
    return '0';
  }

  return `${secondsToMinutes(duration)}`;
};

/**
 * Returns number of seconds left after rounding to nearest minute. Used for telling how many minutes and seconds
 * in a duration there are, use getMinutes(duration) minutes and getSeconds(duration) seconds left in show.
 * @param {Number} duration Duration in seconds.
 * @returns Number of seconds after rounding down to nearest minute. For example getSeconds(65) will return 5.
 */
const getSeconds = duration => {
  if (isNaN(duration)) {
    return 0;
  }

  let durationInSeconds = duration;

  const hours = secondsToHours(durationInSeconds);

  durationInSeconds -= hoursToSeconds(hours);

  const minutes = secondsToMinutes(durationInSeconds);

  durationInSeconds -= minutesToSeconds(minutes);

  return `${Math.floor(durationInSeconds)}`;
};

const getWeekDayMonthDate = (date, timeZone) => moment(date).tz(timeZone)?.format('dddd, MMMM D');

/**
 *
 * @param {Date} date The date to format
 * @returns A formatted date in the form of "Month day", e.g. "April 10" or "December 5"
 */
const getZonedDate = (date, timeZone) => moment(date).tz(timeZone)?.format('MMMM D');

/**
 *
 * @param {Date} date The date to format
 * @returns A formatted date in the form of "Month day", e.g. "April 10" or "December 5"
 */
const getDate = date => {
  const pattern = 'MMMM d';

  return format(date, pattern);
};

/**
 *
 * @param {Date} date The date to format
 * @returns A formatted date for the day of the week, in three letter abbriviation format. Ex. Mon, Tue ...
 */
const dayOfWeekAbbr = date => {
  const pattern = 'E';

  return format(date, pattern);
};

/**
 *
 * @returns A three letter abbreviation for the system time zone.
 */
const localTimeZoneAbbreviation = () => {
  // Use moment, or fallback to toLocaleTimeString.
  const guessedTZ = moment.tz.guess();
  const guessedAbbreviation = guessedTZ == null ? null : moment.tz(guessedTZ).zoneAbbr();

  return guessedAbbreviation ?? new Date().toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2];
};

/**
 *
 * @param {expireTimeUTC} expireTimeUTC UTC timestamp to check if is today, tomorrow, or later
 * @returns A string indicating if the provided date is today, tomorrow, or later
 */
const isTodayTomorrowOrLater = expireTimeUTC => {
  const today = Date.prototype.getCurrent();
  const tomorrow = addDays(today, 1);

  if (!expireTimeUTC) {
    return null;
  }

  const expireDate = fromUnixTime(expireTimeUTC);

  if (isSameDay(expireDate, today)) {
    return 'today';
  } else if (isSameDay(expireDate, tomorrow)) {
    return 'tomorrow';
  } else {
    return 'later';
  }
};

/**
 *
 * @param {date} date A date object to use when calculating the string to return.
 * @returns A string indicating if the provided date is Today, Tonight, Tomorrow, Tomorrow Night, <DayOfWeek>, or <DayOfWeek> Night.
 */
const RelativeDayOrNight = ({ date }) => {
  const tPath = 'timeUtil.dayNightTitles.';
  const { t } = useTranslation();

  // When do we switch between today and tonight?  3am and 3pm.
  const dayNightHourBoundary = 3;
  const hour = getHours(date);
  const isNight = hour < dayNightHourBoundary || hour >= 12 + dayNightHourBoundary;

  // Shift our date by dayNightHourBoundary so shortcuts like isToday/isTomorrow and day name work properly for times between midnight and dayNightHourBoundary AM.
  const shiftedDate = addHours(date, -dayNightHourBoundary);

  const today = Date.prototype.getCurrent();
  const tomorrow = addDays(today, 1);

  const dateIsToday = isSameDay(shiftedDate, today);
  const dateIsTomorrow = isSameDay(shiftedDate, tomorrow);

  if (dateIsToday) {
    return isNight ? t(`${tPath}tonight`) : t(`${tPath}today`);
  } else if (dateIsTomorrow) {
    return isNight ? t(`${tPath}tomorrow night`) : t(`${tPath}tomorrow`);
  } else {
    const pattern = 'EEEE';
    const dayName = format(shiftedDate, pattern);
    const night = t(`${tPath}night`);

    return isNight ? `${dayName} ${night}` : dayName;
  }
};

// Returns 'now' (in UTC) in milliseconds from epoch.
const nowInEpoch = () => Date.now();

const dateFromEpoch = timeEpoch => fromUnixTime(timeEpoch);

/**
 *
 * @param {Object} date The date to start at.
 * @param {Number} timeInterval The number of seconds to add to date
 * @returns Returns a new date that is timeInterval seconds away from original date.
 */
const dateByAddingTimeInterval = (date, timeInterval) => {
  const startDate = date ?? new Date();

  return add(startDate, {
    seconds: timeInterval,
  });
};

/**
 *
 * @param {Object} date The date to format.
 * @param {String} dateFormat The format string.
 * @returns Returns a string of the date in UTC following the specified format.
 */

const utcDateString = (date, dateFormat) => moment(date).tz('UTC').format(dateFormat);

/**
 *
 * @param {number} duration number integer.
 * @param {number} progress number less of 1.
 * @returns Returns a string of the time left about duration.
 */

const episodeTimeLeft = (duration, progress) => {
  if (duration && progress) {
    const left = duration * (1 - progress);
    const hrs = Math.trunc(left / 3600);
    const mins = Math.trunc((left % 3600) / 60);

    return [hrs && `${hrs}h`, mins && `${mins}m`].filter(Boolean).join(' ');
  }
  return '';
};

const systemTimeZone = () => moment.tz.guess();

/**
 * zoned version of date-fns/getHours
 */
const getZonedHours = (date, timeZone) => Number(moment(date).tz(timeZone).format('H'));

/**
 * Is the given date today in the given timezone?
 * zoned version of date-fns/isToday
 * @param {Date|string|number} date
 * @param {string} timeZone
 * @returns {boolean} if today true, else false
 */
const isZonedToday = (date, timeZone) =>
  moment(date).tz(timeZone).format('YYYY-MM-DD') === moment().tz(timeZone).format('YYYY-MM-DD');

/**
 * Return the start of a day for the given date. The result will be in the local timezone
 * @param {Date|string|number} date
 * @param {string} timeZone
 * @returns {Date}
 */
const zonedStartOfDay = (date, timeZone) => {
  const locationTimezoneOffset = moment.tz.zone(timeZone).utcOffset(Date.now());
  const systemTimezoneOffset = moment.tz.zone(systemTimeZone()).utcOffset(Date.now());
  const offsetBetweenLocationAndSystem = locationTimezoneOffset - systemTimezoneOffset;

  return addMinutes(startOfDay(date), offsetBetweenLocationAndSystem);
};

export const TimeUtils = {
  episodeTimeLeft,
  dayOfWeekAbbr,
  getDate,
  getMinuteTime,
  getMinutes,
  getSeconds,
  getLocalTime,
  getTimeFormat,
  getTimeRange,
  getStartTime,
  getWeekDayMonthDate,
  getZonedDate,
  getZonedHourTime,
  getZonedHourTimeAMPM,
  getZonedTime,
  isTodayTomorrowOrLater,
  RelativeDayOrNight,
  localTimeZoneAbbreviation,
  nowInEpoch,
  dateFromEpoch,
  dateByAddingTimeInterval,
  utcDateString,
  secondsToDuration,
  systemTimeZone,
  getZonedHours,
  isZonedToday,
  zonedStartOfDay,
};
